import { FormField, useFormContext } from "@gigpro/ui/Form";
import { useInterviewAvailability, useManagerAvailabilityPreview } from "api/queries/hire";
import type { JobAvailabilityForm } from "domains/hire/availabilitySchema";
import { AvailabilityReadout } from "domains/hire/components/AvailabilityReadout";
import { AvailabilityWindowsField } from "domains/hire/components/AvailabilityWindowsField";
import type { AvailabilityWindow } from "domains/hire/components/AvailabilityWindowsField";
import type React from "react";
import { useFormState } from "react-hook-form";

export interface JobInterviewAvailabilityFieldProps {
  hiringManagerUserId?: number | null;
  label?: React.ReactNode;
  /** Server-resolved inherited default; when set, skips the client-side manager/self lookup. */
  inheritedWindows?: Array<AvailabilityWindow>;
}

export function JobInterviewAvailabilityField({
  hiringManagerUserId,
  label = "Interview availability",
  inheritedWindows: resolvedWindows,
}: Readonly<JobInterviewAvailabilityFieldProps>) {
  const { control } = useFormContext<JobAvailabilityForm>();
  const { isSubmitting } = useFormState({ control });
  const hasResolved = resolvedWindows != null;
  const { data: managerWindows } = useManagerAvailabilityPreview(hiringManagerUserId, !hasResolved);
  const { data: selfWindows } = useInterviewAvailability(!hasResolved && hiringManagerUserId == null);
  const inheritedWindows = resolvedWindows ?? (hiringManagerUserId != null ? managerWindows : selfWindows) ?? [];

  return (
    <FormField
      control={control}
      name="windows"
      render={({ field, fieldState }) => (
        <AvailabilityWindowsField
          label={label}
          description="We'll suggest interview times inside these windows."
          value={field.value}
          onChange={field.onChange}
          error={fieldState.error}
          disabled={isSubmitting}
          emptyState={
            <>
              <p className="text-normal font-semibold text-gray">Using the hiring manager's availability</p>
              <AvailabilityReadout windows={inheritedWindows} emptyLabel="No availability set." />
            </>
          }
        />
      )}
    />
  );
}
